import { FirmDatabase } from '@/database/firmDatabase';
import type { DocumentReference } from 'firebase/firestore';
import { MatterData, HumanData, OrganizationData } from '@/database/baseTypes';
import {
  AddEditMatterFormData,
  HumanContact,
  OrganizationContact,
} from '../types';
import { telemetry } from '@/lib/telemetry';
import { isFirestoreAlreadyExistsError } from '@/utils/firestoreErrors';

export const addOrUpdateMatterContacts = async (
  firmDb: FirmDatabase,
  matterId: string,
  formData: AddEditMatterFormData,
  onSubmitEventId?: string,
) => {
  const humanResults = await addOrUpdateHumans(
    firmDb,
    formData,
    onSubmitEventId,
  );
  const orgResults = await addOrUpdateOrganizations(
    firmDb,
    formData,
    onSubmitEventId,
  );
  await updateMatterData({
    firmDb,
    matterId,
    formData,
    humanResults,
    orgResults,
  });
};

// Private functions

const addOrUpdateHumans = async (
  db: FirmDatabase,
  formData: AddEditMatterFormData,
  onSubmitEventId?: string,
) => {
  const results: {
    formContact: HumanContact;
    firestoreRef: DocumentReference<HumanData>;
  }[] = [];

  const humanContacts = [
    ...formData.clients.flatMap((c) => {
      if (c.type === 'human') {
        return [c];
      }
      return c.representatives ?? [];
    }),
    ...formData.adverseParties.flatMap((c) => {
      if (c.type === 'human') {
        return [c];
      }
      return c.representatives ?? [];
    }),
  ];

  for (const humanContact of humanContacts) {
    if (humanContact.firestoreId) {
      const firestoreRef = await updateExistingHuman(
        db,
        humanContact,
        humanContact.firestoreId,
      );
      results.push({ formContact: humanContact, firestoreRef });
    } else {
      const firestoreRef = await addNewHuman(db, humanContact, onSubmitEventId);
      results.push({ formContact: humanContact, firestoreRef });
    }
  }

  return results;
};

const addNewHuman = async (
  db: FirmDatabase,
  humanContact: HumanContact,
  onSubmitEventId?: string,
) => {
  const humanData: HumanData = {
    firstName: humanContact.firstName,
    lastName: humanContact.lastName,
    emailAddresses: humanContact.emailAddresses,
    primaryEmailAddressId: humanContact.primaryEmailAddressId ?? undefined,
    phoneNumbers: humanContact.phoneNumbers,
    primaryPhoneNumberId: humanContact.primaryPhoneNumberId ?? undefined,
    preferredLanguage: humanContact.preferredLanguage ?? undefined,
  };

  if (!onSubmitEventId) {
    return await db.addHuman(humanData);
  }

  return await addHumanWithIdempotencyRecovery({
    db,
    humanData: {
      ...humanData,
      _idempotency: {
        onSubmitEventId,
        contactTempId: humanContact.tempId,
      },
    },
    onSubmitEventId,
  });
};

const updateExistingHuman = async (
  db: FirmDatabase,
  humanContact: HumanContact,
  humanFirestoreId: string,
) => {
  await db.updateHuman(humanFirestoreId, {
    firstName: humanContact.firstName,
    lastName: humanContact.lastName,
    emailAddresses: humanContact.emailAddresses,
    primaryEmailAddressId: humanContact.primaryEmailAddressId ?? undefined,
    phoneNumbers: humanContact.phoneNumbers,
    primaryPhoneNumberId: humanContact.primaryPhoneNumberId ?? undefined,
    preferredLanguage: humanContact.preferredLanguage ?? undefined,
  });
  return db.query.human(humanFirestoreId);
};

const addOrUpdateOrganizations = async (
  db: FirmDatabase,
  formData: AddEditMatterFormData,
  onSubmitEventId?: string,
) => {
  const results: {
    formContact: OrganizationContact;
    firestoreRef: DocumentReference<OrganizationData>;
  }[] = [];
  for (const orgClients of [
    ...formData.clients.filter((c) => c.type === 'organization'),
    ...formData.adverseParties.filter((c) => c.type === 'organization'),
  ]) {
    if (orgClients.firestoreId) {
      const firestoreRef = await updateExistingOrganization(
        db,
        orgClients,
        orgClients.firestoreId,
      );
      results.push({ formContact: orgClients, firestoreRef });
    } else {
      const firestoreRef = await addNewOrganization(
        db,
        orgClients,
        onSubmitEventId,
      );
      results.push({ formContact: orgClients, firestoreRef });
    }
  }

  return results;
};

const addNewOrganization = async (
  db: FirmDatabase,
  orgContact: OrganizationContact,
  onSubmitEventId?: string,
) => {
  const organizationData: OrganizationData = {
    companyName: orgContact.companyName,
    phoneNumbers: orgContact.phoneNumbers,
    primaryPhoneNumberId: orgContact.primaryPhoneNumberId ?? undefined,
    emailAddresses: orgContact.emailAddresses,
    primaryEmailAddressId: orgContact.primaryEmailAddressId ?? undefined,
  };

  if (!onSubmitEventId) {
    return await db.addOrganization(organizationData);
  }

  return await addOrganizationWithIdempotencyRecovery({
    db,
    organizationData: {
      ...organizationData,
      _idempotency: {
        onSubmitEventId,
        contactTempId: orgContact.tempId,
      },
    },
    onSubmitEventId,
  });
};

const updateExistingOrganization = async (
  db: FirmDatabase,
  orgContact: OrganizationContact,
  orgFirestoreId: string,
) => {
  await db.updateOrganization(orgFirestoreId, {
    companyName: orgContact.companyName,
    phoneNumbers: orgContact.phoneNumbers,
    primaryPhoneNumberId: orgContact.primaryPhoneNumberId ?? undefined,
    emailAddresses: orgContact.emailAddresses,
    primaryEmailAddressId: orgContact.primaryEmailAddressId ?? undefined,
  });
  return db.query.organization(orgFirestoreId);
};

const updateMatterData = async (props: {
  firmDb: FirmDatabase;
  matterId: string;
  formData: AddEditMatterFormData;
  humanResults: {
    formContact: HumanContact;
    firestoreRef: DocumentReference<HumanData>;
  }[];
  orgResults: {
    formContact: OrganizationContact;
    firestoreRef: DocumentReference<OrganizationData>;
  }[];
}) => {
  const { firmDb, matterId, formData, humanResults, orgResults } = props;
  // Maintain the order of contacts as they appear in the form
  const contactIdsInOrder = formData.clients.flatMap((c) => {
    if (c.type === 'human') {
      return [c.tempId];
    }
    return [c.tempId, ...(c.representatives?.map((r) => r.tempId) ?? [])];
  });
  contactIdsInOrder.push(
    ...formData.adverseParties.flatMap((c) => {
      if (c.type === 'human') {
        return [c.tempId];
      }
      return [c.tempId, ...(c.representatives?.map((r) => r.tempId) ?? [])];
    }),
  );

  const humansAndOrganizations: MatterData['humansAndOrganizations'] =
    contactIdsInOrder.map((id) => {
      const humanResult = humanResults.find(
        (result) => result.formContact.tempId === id,
      );
      if (humanResult) {
        return {
          id: humanResult.firestoreRef.id,
          type: 'human',
          role: humanResult.formContact.role,
        };
      }
      const orgResult = orgResults.find(
        (result) => result.formContact.tempId === id,
      );
      if (!orgResult) {
        throw new Error(`orgResult is undefined for id: ${id}`);
      }
      return {
        id: orgResult.firestoreRef.id,
        type: 'organization',
        role: orgResult.formContact.role,
      };
    });

  const combinedResults = [...humanResults, ...orgResults];

  const updateData: Partial<MatterData> = { humansAndOrganizations };

  const organizationRepresentatives: MatterData['organizationRepresentatives'] =
    {};
  for (const orgResult of orgResults) {
    const orgId = orgResult.firestoreRef.id;
    const representatives = orgResult.formContact.representatives;
    if (!representatives || representatives.length === 0) {
      continue;
    }
    organizationRepresentatives[orgId] = representatives.flatMap(
      (humanContact) => {
        const humanResult = humanResults.find(
          (result) => result.formContact.tempId === humanContact.tempId,
        );
        if (humanResult) {
          return [humanResult.firestoreRef.id];
        }
        return [];
      },
    );
  }

  updateData.organizationRepresentatives = organizationRepresentatives;

  if (formData.primaryClientTempId) {
    updateData.primaryClientId = combinedResults.find(
      (result) =>
        result.formContact.tempId === formData.primaryClientTempId &&
        result.formContact.role === 'client',
    )?.firestoreRef.id;
  }

  if (formData.primaryAdversePartyTempId) {
    updateData.primaryAdversePartyId = combinedResults.find(
      (result) =>
        result.formContact.tempId === formData.primaryAdversePartyTempId &&
        result.formContact.role === 'adverseParty',
    )?.firestoreRef.id;
  }

  await firmDb.updateMatter(matterId, updateData);
};

const addHumanWithIdempotencyRecovery = async (props: {
  db: FirmDatabase;
  humanData: HumanData;
  onSubmitEventId: string;
}) => {
  const { db, humanData, onSubmitEventId } = props;

  try {
    return await db.addHuman(humanData);
  } catch (error) {
    if (!isFirestoreAlreadyExistsError(error)) {
      throw error;
    }

    const matchingHumans = await db.getHumans([
      [
        '_idempotency.onSubmitEventId' as keyof HumanData,
        '==',
        onSubmitEventId,
      ],
    ]);

    if (matchingHumans.empty) {
      telemetry.error({
        error,
        message:
          'addOrUpdateMatterContacts: addHuman returned already-exists, but no human was found by onSubmitEventId lookup',
        extra: {
          onSubmitEventId,
          contactTempId: humanData._idempotency?.contactTempId,
          matchingHumanCount: matchingHumans.size,
          addHumanError: String(error),
          humanData,
        },
      });
      throw error;
    }

    const exactMatches = matchingHumans.docs.filter((doc) =>
      isSameHumanData(doc.data(), humanData),
    );

    if (exactMatches.length === 0) {
      telemetry.error({
        error,
        message:
          'addOrUpdateMatterContacts: addHuman returned already-exists, but no exact matching human was found during recovery',
        extra: {
          onSubmitEventId,
          contactTempId: humanData._idempotency?.contactTempId,
          matchingHumanCount: matchingHumans.size,
          addHumanError: String(error),
          humanData,
        },
      });
      throw error;
    }

    const recoveredHumanRef = exactMatches[0].ref;
    telemetry.warn({
      message:
        'addOrUpdateMatterContacts: addHuman returned already-exists and was recovered by onSubmitEventId lookup',
      extra: {
        onSubmitEventId,
        contactTempId: humanData._idempotency?.contactTempId,
        recoveredHumanId: recoveredHumanRef.id,
        matchingHumanCount: matchingHumans.size,
        exactMatchCount: exactMatches.length,
        addHumanError: String(error),
      },
    });
    return recoveredHumanRef;
  }
};

const addOrganizationWithIdempotencyRecovery = async (props: {
  db: FirmDatabase;
  organizationData: OrganizationData;
  onSubmitEventId: string;
}) => {
  const { db, organizationData, onSubmitEventId } = props;

  try {
    return await db.addOrganization(organizationData);
  } catch (error) {
    if (!isFirestoreAlreadyExistsError(error)) {
      throw error;
    }

    const matchingOrganizations = await db.getOrganizations([
      [
        '_idempotency.onSubmitEventId' as keyof OrganizationData,
        '==',
        onSubmitEventId,
      ],
    ]);

    if (matchingOrganizations.empty) {
      telemetry.error({
        error,
        message:
          'addOrUpdateMatterContacts: addOrganization returned already-exists, but no organization was found by onSubmitEventId lookup',
        extra: {
          onSubmitEventId,
          contactTempId: organizationData._idempotency?.contactTempId,
          matchingOrganizationCount: matchingOrganizations.size,
          addOrganizationError: String(error),
          organizationData,
        },
      });
      throw error;
    }

    const exactMatches = matchingOrganizations.docs.filter((doc) =>
      isSameOrganizationData(doc.data(), organizationData),
    );

    if (exactMatches.length === 0) {
      telemetry.error({
        error,
        message:
          'addOrUpdateMatterContacts: addOrganization returned already-exists, but no exact matching organization was found during recovery',
        extra: {
          onSubmitEventId,
          contactTempId: organizationData._idempotency?.contactTempId,
          matchingOrganizationCount: matchingOrganizations.size,
          addOrganizationError: String(error),
          organizationData,
        },
      });
      throw error;
    }

    const recoveredOrganizationRef = exactMatches[0].ref;
    telemetry.warn({
      message:
        'addOrUpdateMatterContacts: addOrganization returned already-exists and was recovered by onSubmitEventId lookup',
      extra: {
        onSubmitEventId,
        contactTempId: organizationData._idempotency?.contactTempId,
        recoveredOrganizationId: recoveredOrganizationRef.id,
        matchingOrganizationCount: matchingOrganizations.size,
        exactMatchCount: exactMatches.length,
        addOrganizationError: String(error),
      },
    });
    return recoveredOrganizationRef;
  }
};

const isSameHumanData = (left: HumanData, right: HumanData) =>
  left.firstName === right.firstName &&
  left.lastName === right.lastName &&
  left.primaryEmailAddressId === right.primaryEmailAddressId &&
  left.primaryPhoneNumberId === right.primaryPhoneNumberId &&
  left.preferredLanguage === right.preferredLanguage &&
  left._idempotency?.onSubmitEventId === right._idempotency?.onSubmitEventId &&
  left._idempotency?.contactTempId === right._idempotency?.contactTempId &&
  isSameJsonValue(left.emailAddresses, right.emailAddresses) &&
  isSameJsonValue(left.phoneNumbers, right.phoneNumbers);

const isSameOrganizationData = (
  left: OrganizationData,
  right: OrganizationData,
) =>
  left.companyName === right.companyName &&
  left.primaryEmailAddressId === right.primaryEmailAddressId &&
  left.primaryPhoneNumberId === right.primaryPhoneNumberId &&
  left._idempotency?.onSubmitEventId === right._idempotency?.onSubmitEventId &&
  left._idempotency?.contactTempId === right._idempotency?.contactTempId &&
  isSameJsonValue(left.emailAddresses, right.emailAddresses) &&
  isSameJsonValue(left.phoneNumbers, right.phoneNumbers);

const isSameJsonValue = (left: unknown, right: unknown) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
